import React, { useState, useEffect, useCallback } from 'react';
import './RunDetails.css';

function formatDate(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function formatDuration(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const ms = new Date(endIso) - new Date(startIso);
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

// ── Inline input/output viewer ────────────────────────────────────────────────
function IoBlock({ url, label }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetch(url)
      .then(r => r.json())
      .then(json => setData(JSON.stringify(json, null, 2)))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [url]);

  return (
    <div className="io-block">
      <span className="io-block-label">{label}</span>
      {loading && <div className="io-loading">Loading…</div>}
      {error   && <div className="io-error">{error}</div>}
      {data    && <pre className="io-json">{data}</pre>}
    </div>
  );
}

// ── Single failed run row (expandable) ───────────────────────────────────────
function FailedRunRow({ run, app, apiCall, isExpanded, onToggle }) {
  const [actions, setActions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const loadActions = useCallback(async () => {
    if (actions) return;
    setLoading(true);
    setError(null);
    try {
      const { subscriptionId, resourceGroup, workflowName } = app;
      const result = await apiCall(
        `actions?subscriptionId=${subscriptionId}&resourceGroup=${encodeURIComponent(resourceGroup)}&workflowName=${encodeURIComponent(workflowName)}&runName=${encodeURIComponent(run.name)}`
      );
      setActions(result.failedActions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [run.name, app, apiCall, actions]);

  const handleToggle = () => {
    if (!isExpanded) loadActions();
    onToggle();
  };

  return (
    <div className={`run-row ${isExpanded ? 'expanded' : ''}`}>
      <button className="run-row-header" onClick={handleToggle}>
        <div className="run-row-info">
          <span className="run-badge failed">Failed</span>
          <div className="run-row-times">
            <span className="run-start">{formatDate(run.startTime)}</span>
            {run.endTime && (
              <span className="run-duration">{formatDuration(run.startTime, run.endTime)}</span>
            )}
          </div>
          {run.triggerName && (
            <span className="run-trigger" title="Trigger">{run.triggerName}</span>
          )}
        </div>
        <span className="run-row-chevron">{isExpanded ? '\u25b2' : '\u25bc'}</span>
      </button>

      {isExpanded && (
        <div className="run-row-body">
          {loading && <div className="run-loading">Loading failed steps...</div>}
          {error && !loading && <div className="run-error">{error}</div>}

          {actions && !loading && actions.length === 0 && (
            <p className="run-no-actions">No failed action details available.</p>
          )}

          {actions && !loading && actions.length > 0 && (
            <div className="action-list">
              {(actions.filter(a => a.errorMessage !== 'An action failed. No dependent actions succeeded.').length > 0
                ? actions.filter(a => a.errorMessage !== 'An action failed. No dependent actions succeeded.')
                : actions
              ).map((action) => (
                <div key={action.name} className="action-item">
                  <div className="action-item-header">
                    <span className="action-name">{action.name}</span>
                    {action.type && <span className="action-type">{action.type}</span>}
                    {action.startTime && action.endTime && (
                      <span className="action-duration">
                        {formatDuration(action.startTime, action.endTime)}
                      </span>
                    )}
                  </div>
                  {(action.errorCode || action.errorMessage) && (
                    <pre className="action-error">
                      {[action.errorCode, action.errorMessage].filter(Boolean).join(': ')}
                    </pre>
                  )}
                  {action.inputsLink  && <IoBlock url={action.inputsLink}  label="Inputs"  />}
                  {action.outputsLink && <IoBlock url={action.outputsLink} label="Outputs" />}
                </div>
              ))}
            </div>
          )}

          <div className="run-row-meta">
            <span>Run ID: <code>{run.name}</code></span>
            <a
              className="action-link"
              href={[
                'https://portal.azure.com/#view/Microsoft_Azure_EMA/DesignerEditorConsumption.ReactView',
                '/id/', encodeURIComponent(`/subscriptions/${app.subscriptionId}/resourceGroups/${app.resourceGroup}/providers/Microsoft.Logic/workflows/${app.workflowName}`),
                '/location/', app.location,
                '/isReadOnly~/true/isMonitoringView~/true',
                '/runId/', encodeURIComponent(`/subscriptions/${app.subscriptionId}/resourceGroups/${app.resourceGroup}/providers/Microsoft.Logic/workflows/${app.workflowName}/runs/${run.name}`),
              ].join('')}
              target="_blank"
              rel="noreferrer"
            >
              Open in Azure
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main slide-in panel ──────────────────────────────────────────────────────
function RunDetails({ app, failedRuns, apiCall, onClose }) {
  const [expandedRun, setExpandedRun] = useState(null);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const sorted = failedRuns.slice().sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

  return (
    <>
      <div className="run-details-backdrop" onClick={onClose} />
      <div className="run-details-panel">
        <div className="run-details-header">
          <div>
            <h2 className="run-details-title">{app.displayName}</h2>
            <p className="run-details-subtitle">
              {failedRuns.length} failed run{failedRuns.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button className="run-details-close" onClick={onClose} aria-label="Close">&#10005;</button>
        </div>

        <div className="run-details-body">
          {sorted.length === 0 ? (
            <p className="run-details-empty">No failed runs in this period.</p>
          ) : (
            sorted.map((run) => (
              <FailedRunRow
                key={run.name}
                run={run}
                app={app}
                apiCall={apiCall}
                isExpanded={expandedRun === run.name}
                onToggle={() => setExpandedRun(prev => prev === run.name ? null : run.name)}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default RunDetails;
